// NewsletterViewer — a newsletter issue shown like an email in a mail client.
//
//   [ Vorschau | HTML | Text ]            [Änderungen speichern] [Exportieren ▾]  (shared ViewerToolbar)
//   ┌ mail window ──────────────────────────────────────────────┐
//   │ Absender · an dich                              Desktop|Mobil │
//   │ Betreff (type to change)                                  │
//   │ Vorschautext (type to change)                             │
//   ├───────────────────────────────────────────────────────────┤
//   │ the mail itself — click any text to type                  │
//   └───────────────────────────────────────────────────────────┘
//
// Same tab structure as the blog viewer. Changing text with an instruction
// ("KI-Anweisung") works through the existing refine box in Produktion.js.
// Data: blog_structured + `newsletter` extras {subject, preheader, cta_label,
// cta_lead, cta_url, sender_address, imprint_url}.
// Exposes save / getBlogData / getCleanHtml like BlogArticleViewer.

import { useState, useMemo, useEffect, useLayoutEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Copy, FileDown, FileText, Monitor, Smartphone } from 'lucide-react';
import ViewerToolbar from '../common/ViewerToolbar';
import { buildNewsletterHtml, buildNewsletterText, getNewsletterMeta, applyNewsletterEdit, isSingleLinePath, blogToEditableText, editableTextToBlog } from './emailHtml';

const slugify = (s) => String(s || 'newsletter').toLowerCase()
  .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
  .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'newsletter';

// Quiet length hint: green inside the recommended range, amber outside.
const Count = ({ value, min, max }) => {
  const n = (value || '').length;
  return <span className={`shrink-0 text-xs ${n >= min && n <= max ? 'text-emerald-600' : 'text-amber-600'}`}>{n} / {min}–{max}</span>;
};

const bare = 'w-full bg-transparent border-0 border-b border-transparent px-0 py-0.5 hover:border-slate-200 focus:border-[#1e9ead] focus:outline-none focus:ring-0 placeholder:text-slate-300';

// A text field that grows with its content, so the whole subject / preview
// text is always visible (no cut-off at the field edge). Enter is blocked:
// both are single-line values.
const AutoText = ({ value, onChange, className, ...rest }) => {
  const el = useRef(null);
  const fit = () => { if (el.current) { el.current.style.height = 'auto'; el.current.style.height = `${el.current.scrollHeight}px`; } };
  useLayoutEffect(fit, [value]);
  useEffect(() => {
    if (!el.current || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(fit);
    ro.observe(el.current);
    return () => ro.disconnect();
  }, []);
  return (
    <textarea ref={el} rows={1} value={value} className={`${className} resize-none overflow-hidden`}
      onChange={(e) => onChange(e.target.value.replace(/\s*\n+\s*/g, ' '))}
      onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }} {...rest} />
  );
};

const NewsletterViewer = forwardRef(function NewsletterViewer(
  { blogData: initialBlog, fallbackTitle = '', heroImageUrl = '', language, brandName = '', defaultCtaUrl = '', onSave, articleKey },
  ref
) {
  const [tab, setTab] = useState('preview');
  const [device, setDevice] = useState('desktop');
  const [blog, setBlog] = useState(initialBlog);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [textDraft, setTextDraft] = useState('');
  const [frameH, setFrameH] = useState(700);
  const [frameVersion, setFrameVersion] = useState(0);
  const frameRef = useRef(null);
  const blogRef = useRef(blog);
  blogRef.current = blog;
  const lastKey = useRef(articleKey);

  // New content piece or an AI rewrite arrived → take it over and redraw the mail.
  useEffect(() => {
    if (articleKey !== lastKey.current) {
      lastKey.current = articleKey;
      setBlog(initialBlog);
      setDirty(false);
      setFrameVersion((v) => v + 1);
    }
  }, [articleKey, initialBlog]);

  const lang = language === 'en' ? 'en' : 'de';
  const nl = blog?.newsletter || {};
  const info = getNewsletterMeta(blog);

  const renderOpts = useMemo(() => ({
    lang,
    fallbackTitle,
    heroImageUrl,
    brandName: brandName || undefined,
    ctaUrl: nl.cta_url || defaultCtaUrl || '',
    senderAddress: nl.sender_address || '',
    imprintUrl: nl.imprint_url || '',
  }), [lang, fallbackTitle, heroImageUrl, brandName, nl.cta_url, nl.sender_address, nl.imprint_url, defaultCtaUrl]);

  // Export HTML always reflects the latest edits.
  const html = useMemo(() => (blog ? buildNewsletterHtml(blog, renderOpts) : ''), [blog, renderOpts]);
  const plainText = useMemo(() => (blog ? buildNewsletterText(blog, renderOpts) : ''), [blog, renderOpts]);

  // The iframe is NOT redrawn while the user types in it (that would reset the
  // caret). It is redrawn only for outside changes: AI rewrite, button link, …
  const frameHtml = useMemo(
    () => (blogRef.current ? buildNewsletterHtml(blogRef.current, { ...renderOpts, editable: true }) : ''),
    [frameVersion, renderOpts] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const change = (fn) => { setBlog((prev) => fn(prev)); setDirty(true); };
  const setNl = (patch) => change((prev) => ({ ...(prev || {}), newsletter: { ...((prev || {}).newsletter || {}), ...patch } }));

  const persist = async () => {
    if (!onSave || !blogRef.current) return;
    setSaving(true);
    try { await onSave(blogRef.current); setDirty(false); } catch (_) { /* parent shows the toast */ } finally { setSaving(false); }
  };

  useImperativeHandle(ref, () => ({
    save: persist,
    getBlogData: () => blogRef.current,
    getCleanHtml: () => buildNewsletterHtml(blogRef.current, { ...renderOpts, standalone: false }),
  }), [renderOpts, onSave]); // eslint-disable-line react-hooks/exhaustive-deps

  // Make every marked text in the preview typeable and write edits back.
  const wireFrame = () => {
    const doc = frameRef.current?.contentDocument;
    if (!doc) return;
    const measure = () => setFrameH(doc.documentElement.scrollHeight + 4);
    doc.querySelectorAll('[data-nl]').forEach((el) => {
      const path = el.getAttribute('data-nl');
      el.setAttribute('contenteditable', 'true');
      el.setAttribute('spellcheck', 'true');
      el.addEventListener('input', () => {
        change((prev) => applyNewsletterEdit(prev, path, el.innerText));
        measure();
      });
      el.addEventListener('keydown', (e) => { if (e.key === 'Enter' && isSingleLinePath(path)) e.preventDefault(); });
      el.addEventListener('paste', (e) => {
        e.preventDefault();
        const plain = (e.clipboardData || window.clipboardData).getData('text/plain');
        doc.execCommand('insertText', false, isSingleLinePath(path) ? plain.replace(/\s+/g, ' ') : plain);
      });
    });
    // Links must not navigate away inside the preview.
    // (an in-page anchor in a srcdoc frame would otherwise reload the whole app)
    doc.querySelectorAll('a').forEach((a) => a.addEventListener('click', (e) => {
      e.preventDefault();
      const href = a.getAttribute('href') || '';
      if (href.startsWith('#nl-')) doc.getElementById(href.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }));
    measure();
  };
  useEffect(() => { const t = setTimeout(() => { const d = frameRef.current?.contentDocument; if (d?.documentElement) setFrameH(d.documentElement.scrollHeight + 4); }, 80); return () => clearTimeout(t); }, [device, tab, frameHtml]);

  const download = () => {
    const url = URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url; a.download = `${slugify(info.subject || fallbackTitle)}.html`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  // PDF: opens the print-ready mail in a new tab and calls print → "Als PDF sichern".
  const exportPdf = () => {
    const doc = buildNewsletterHtml(blogRef.current, { ...renderOpts, print: true });
    const url = URL.createObjectURL(new Blob([doc], { type: 'text/html;charset=utf-8' }));
    const w = window.open(url, '_blank', 'noopener,noreferrer');
    if (!w) {
      const a = document.createElement('a');
      a.href = url; a.download = `${slugify(info.subject || fallbackTitle)}.html`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    } else {
      const tryPrint = () => { try { w.focus(); w.print(); } catch (_) { /* user prints manually */ } };
      try { w.addEventListener('load', () => setTimeout(tryPrint, 300)); } catch (_) { /* cross-origin guard */ }
      setTimeout(tryPrint, 1800);
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };
  const copyText = async (value) => {
    try { await navigator.clipboard.writeText(value); return true; } catch (_) { return false; }
  };

  // Tabs: entering "Text" shows the current issue as text; leaving it redraws the preview.
  const goTab = (key) => {
    if (key === 'text' && tab !== 'text') setTextDraft(blogToEditableText(blogRef.current));
    if (tab === 'text' && key !== 'text') setFrameVersion((v) => v + 1);
    setTab(key);
  };

  if (!blog) return null;
  const subject = nl.subject ?? info.subject;
  const preheader = nl.preheader ?? info.preheader;
  const sender = brandName || 'xchannels.ai';
  const ctaMissing = !renderOpts.ctaUrl;

  const iconBtn = (key, Icon, label) => (
    <button type="button" title={label} aria-label={label} onClick={() => setDevice(key)}
      className={`rounded p-1.5 ${device === key ? 'bg-slate-100 text-slate-900' : 'text-slate-400 hover:text-slate-700'}`}><Icon size={16} /></button>
  );

  return (
    <div className="space-y-4" data-testid="newsletter-viewer">
      <ViewerToolbar
        testIdPrefix="newsletter"
        tabs={[{ key: 'preview', label: 'Vorschau' }, { key: 'html', label: 'HTML' }, { key: 'text', label: 'Text' }]}
        active={tab}
        onTab={goTab}
        dirty={dirty}
        saving={saving}
        onSave={onSave ? persist : undefined}
        exportItems={[
          { label: 'HTML kopieren', icon: <Copy size={14} />, onClick: () => copyText(html), doneLabel: 'Kopiert', testId: 'newsletter-copy-html' },
          { label: 'PDF exportieren', icon: <FileDown size={14} />, onClick: exportPdf, testId: 'newsletter-export-pdf' },
          { label: 'HTML herunterladen', icon: <FileDown size={14} />, onClick: download, testId: 'newsletter-download-html' },
          { label: 'Als Text kopieren', icon: <FileText size={14} />, onClick: () => copyText(plainText), doneLabel: 'Kopiert', testId: 'newsletter-copy-text' },
        ]}
      />

      {/* The mail window: header like an email client, then the mail */}
      <div className="overflow-hidden rounded-xl bg-white shadow-lg">
        <header className="border-b border-slate-200 px-5 py-4 sm:px-6" data-testid="newsletter-inbox-preview">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#0a1628] text-sm font-semibold text-white">{sender.charAt(0).toUpperCase()}</span>
              <div className="min-w-0 leading-tight">
                <div className="truncate text-sm font-semibold text-slate-900">{sender}</div>
                <div className="text-xs text-slate-500">an dich</div>
              </div>
            </div>
            {tab === 'preview' && <div className="flex shrink-0 items-center gap-0.5">{iconBtn('desktop', Monitor, 'Desktop')}{iconBtn('mobile', Smartphone, 'Mobil')}</div>}
          </div>
          <div className="flex items-baseline gap-3">
            <AutoText className={`${bare} text-xl font-semibold leading-snug text-slate-900`} value={subject} placeholder="Betreff"
              onChange={(v) => setNl({ subject: v })} data-testid="newsletter-subject" aria-label="Betreff" />
            <Count value={subject} min={30} max={50} />
          </div>
          <div className="mt-1 flex items-baseline gap-3">
            <AutoText className={`${bare} text-sm leading-snug text-slate-500`} value={preheader} placeholder="Vorschautext – steht im Postfach neben dem Betreff"
              onChange={(v) => setNl({ preheader: v })} data-testid="newsletter-preheader" aria-label="Vorschautext" />
            <Count value={preheader} min={50} max={90} />
          </div>
        </header>

        {tab === 'preview' && (
          <div className="bg-[#eef1f5] p-3 sm:p-5">
            <div className="flex justify-center">
              <iframe
                ref={frameRef}
                key={`${frameVersion}-${device}`}
                title="Newsletter-Vorschau"
                srcDoc={frameHtml}
                sandbox="allow-same-origin"
                onLoad={wireFrame}
                scrolling="no"
                className="bg-white"
                style={{ width: device === 'mobile' ? 375 : 640, maxWidth: '100%', height: frameH, border: 0 }}
                data-testid="newsletter-preview-frame"
              />
            </div>
            <p className="mt-3 text-center text-xs text-slate-500">
              Klick in einen Text, um ihn zu ändern.{ctaMissing && <span className="text-amber-600"> · Button-Link fehlt: unten unter „Button-Link und Footer“ eintragen.</span>}
            </p>
          </div>
        )}

        {tab === 'html' && (
          <div className="p-4 sm:p-5">
            <textarea readOnly value={html} className="h-[520px] w-full resize-y rounded-md border border-slate-200 bg-slate-50 p-3 font-mono text-xs text-slate-800" data-testid="newsletter-html-source" aria-label="HTML-Quelltext" />
          </div>
        )}

        {tab === 'text' && (
          <div className="p-4 sm:p-5">
            <p className="mb-2 text-xs text-slate-500">
              Der ganze Newsletter als Text. Änderungen erscheinen in der Vorschau. <code>#</code> Überschrift · <code>##</code> Abschnitt · <code>-</code> Liste · Leerzeile = neuer Absatz
            </p>
            <textarea value={textDraft}
              onChange={(e) => { const v = e.target.value; setTextDraft(v); change((prev) => editableTextToBlog(v, prev)); }}
              className="h-[560px] w-full resize-y rounded-md border border-slate-200 bg-white p-3 font-mono text-sm leading-relaxed text-slate-900 focus:border-[#1e9ead] focus:outline-none" data-testid="newsletter-text-source" aria-label="Newsletter als Text" />
          </div>
        )}
      </div>

      {/* Delivery details, only what the mail legally needs — kept out of the way */}
      <details className="rounded-lg border border-white/10 bg-[#1A2B42] px-4 py-3" data-testid="newsletter-settings">
        <summary className="cursor-pointer text-sm text-white">Button-Link und Footer</summary>
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          {[
            ['Button-Link', 'cta_url', 'https://…', 'newsletter-cta-url'],
            ['Link zum Impressum', 'imprint_url', 'https://…/impressum'],
          ].map(([label, key, ph, tid]) => (
            <label key={key} className="block text-xs text-[#A0AEC0]">{label}
              <input className="mt-1 w-full rounded-md border border-white/10 bg-[#0A1628] px-3 py-2 text-sm text-white placeholder:text-[#5A6C7D] focus:border-[#00D9C8] focus:outline-none"
                placeholder={ph} value={nl[key] ?? ''} onChange={(e) => setNl({ [key]: e.target.value })} data-testid={tid} />
            </label>
          ))}
          <label className="block text-xs text-[#A0AEC0] md:col-span-2">Absenderadresse (Pflicht im Footer)
            <textarea rows={2} className="mt-1 w-full rounded-md border border-white/10 bg-[#0A1628] px-3 py-2 text-sm text-white placeholder:text-[#5A6C7D] focus:border-[#00D9C8] focus:outline-none"
              placeholder={'Firma GmbH\nMusterstraße 1, 10115 Berlin'} value={nl.sender_address ?? ''} onChange={(e) => setNl({ sender_address: e.target.value })} />
          </label>
          <p className="text-xs text-[#5A6C7D] md:col-span-2">Der Abmelde-Link bleibt als <code>{'{{unsubscribe_url}}'}</code> im HTML. Dein Versandtool ersetzt ihn beim Versand.</p>
        </div>
      </details>
    </div>
  );
});

export default NewsletterViewer;
